import type { EventDetails } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { isBefore } from "date-fns"
import dynamic from "next/dynamic"
import { useParams } from "next/navigation"
import { useState } from "react"

import IconHeart from "@jobteaser/ui-kit/assets/icons/jds_heart.svg"
import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"

import { NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS } from "@/modules/careerCenter/constants/flags"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { isWwwCareerCenter } from "@/modules/careerCenter/isWwwCareerCenter"
import { WITHOUT_REGISTRATION } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTrackGenericClickEvent } from "@/modules/tracking/useTrackGenericClickEvent"
import { useFlag } from "@/modules/unleash/hooks/useFlag"

import styles from "./MainButtonBlock.module.css"

const DynamicLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/LoginModal").then(mod => mod.LoginModal),
  { ssr: false }
)

const DynamicNewLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/NewLoginModal").then(mod => mod.NewLoginModal),
  { ssr: false }
)

export const MainButtonBlockUnlogged: FunctionComponent = () => {
  const { t } = useTranslation(["event_details"])
  const [isLoginFlowOpen, setIsLoginFlowOpen] = useState(false)
  const trackEvent = useTrackGenericClickEvent()

  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const { dataCareerCenter } = useCareerCenterFetch()
  const isNewSignInFlowEnabled = useFlag(NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS.ENABLED)

  const { registrationDetails } = event as EventDetails
  const { type } = registrationDetails

  const isWithoutRegistration = type === WITHOUT_REGISTRATION

  const currentDate = new Date()
  const startDate = new Date(event?.startDate || "")
  const hasEventNotStarted = isBefore(currentDate, startDate)
  const isRegistrationDeadlineNotExceeded =
    isWithoutRegistration || isBefore(currentDate, new Date(registrationDetails.registrationDeadline))

  const isDisplayedFakeSubscribeButton = !isWithoutRegistration
  const isDisplayedFakeSaveButton = hasEventNotStarted && isRegistrationDeadlineNotExceeded

  const onClick = (eventType: "save" | "subscription"): void => {
    const roleName = eventType === "save" ? "event_ad_save" : "event_ad_subscription_click"
    trackEvent(roleName)
    setIsLoginFlowOpen(true)
  }

  if (!isDisplayedFakeSubscribeButton && !isDisplayedFakeSaveButton) {
    return null
  }

  return (
    <>
      {/* Login Flow */}
      {isLoginFlowOpen && isNewSignInFlowEnabled && (
        <DynamicNewLoginModal
          careerCenterName={dataCareerCenter?.name || ""}
          isWwwCareerCenter={isWwwCareerCenter(dataCareerCenter)}
          onClose={() => setIsLoginFlowOpen(false)}
        />
      )}
      {isLoginFlowOpen && !isNewSignInFlowEnabled && (
        <DynamicLoginModal
          careerCenterName={dataCareerCenter?.name || ""}
          isWwwCareerCenter={isWwwCareerCenter(dataCareerCenter)}
          onClose={() => setIsLoginFlowOpen(false)}
        />
      )}

      <div className={styles.buttons}>
        {isDisplayedFakeSubscribeButton && (
          <Button
            onClick={() => onClick("subscription")}
            title={t("event_details.subscribe")}
            data-testid="event-details-subscribe-button-unlogged"
            role="button"
          >
            {t("event_details.subscribe")}
          </Button>
        )}
        {isDisplayedFakeSaveButton && (
          <Button
            onClick={() => onClick("save")}
            icon={IconHeart}
            variant={BUTTON_VARIANT.GHOST}
            className={styles.saveButton}
            title={t("event_details.button.save")}
            data-testid="event-details-save-button-unlogged"
            role="button"
          >
            {t("event_details.button.save")}
          </Button>
        )}
      </div>
    </>
  )
}
